<template>
    <VJoyPanel class="team-panel" :class="{isDesktopScreen}">
        <template #panel-title>{{ $t('projectclient.quote.view.project-team.title') }}</template>
        <template #panel-body>
            <VJoyWrapper column-gap="6">
                <VJoyWrapper row-gap="0" no-margin direction="column" align="flex-start">
                    <p class="team-panel__role">{{ $t('projectclient.quote.view.project-team.freelancer') }}</p>
                    <VJoyWrapper justify="space-between" wrap="nowrap" no-margin class="full-width">
                        <div class="team-panel__info-container / flex">
                            <VJoyAvatar
                                class="team-panel__info-container__avatar"
                                size="small"
                                :photo-url="quote.freelancer.photoUrl"
                                :full-name="freelancerName"
                            />
                            <div class="team-panel__info-container__about / flex">
                                <p class="team-panel__info-container__name ellipsis" title="Freelancer Full Name">{{ freelancerName }}</p>
                                <p title="Freelancer Headline" class="team-panel__info-container__job-title ellipsis">
                                    {{ quote.freelancer.headLine }}
                                </p>
                            </div>
                        </div>
                        <div class="team-panel__actions / flex" :class="{isDesktopScreen}">
                            <VJoyIconButton size="small" variant="ghost" icon="user-lighter" @click="displayFreelancerProfileDrawer" />
                            <VJoyIconButton size="small" variant="ghost" icon="message" @click="displayFreelancerMessengerDrawer" />
                        </div>
                    </VJoyWrapper>
                </VJoyWrapper>
                <VJoyWrapper row-gap="0" no-margin direction="column" align="flex-start">
                    <p class="team-panel__role">{{ $t('projectclient.quote.view.project-team.project-owner') }}</p>
                    <VJoyWrapper justify="space-between" wrap="nowrap" no-margin class="full-width">
                        <div class="team-panel__info-container / flex">
                            <VJoyAvatar
                                class="team-panel__info-container__avatar"
                                size="small"
                                :photo-url="quote.client.photoUrl"
                                :full-name="clientFullName"
                            />
                            <div title="Client Full Name" class="team-panel__info-container__about / flex">
                                <p class="team-panel__info-container__name ellipsis" data-testid="project-owner-fullname">
                                    {{ clientFullName }}
                                </p>
                                <p title="Freelancer Headline" class="team-panel__info-container__job-title ellipsis">
                                    {{ quote.client.company.name }}
                                </p>
                            </div>
                        </div>
                        <div class="team-panel__actions / flex">
                            <VJoyIconButton size="small" variant="ghost" icon="pen" @click="handleOpenDialog" />
                        </div>
                    </VJoyWrapper>
                </VJoyWrapper>
            </VJoyWrapper>
        </template>
    </VJoyPanel>
    <ChangeQuoteOwnerModal :current-quote-owner="clientFullName" :quote-id="quote.id" />
    <FreelancerDrawer
        :freelancer-info="computedFreelancerInfo"
        :conversation-id="quote.conversationId"
        :client-identity-id="quote.client.identityId"
        :is-client-owner="isClientQuoteOwner"
    />
</template>

<script setup lang="ts">
    import {computed, onUnmounted, onMounted} from 'vue';
    import {useScreenSizeStore} from '@common/stores';
    import {FreelancerDrawer, FreelancerDrawerTab} from '@common/ui';
    import {storeToRefs} from 'pinia';
    import {VJoyIconButton, VJoyWrapper, VJoyAvatar, VJoyPanel} from '@maltjoy/core-vue';
    import type {QuoteResource} from '@api';
    import {useClientQuoteViewStore, useCompanyInfoStore} from '../../stores';
    import {ChangeQuoteOwnerModal} from './dialogs/ChangeQuoteOwnerModal';
    import {eventBus} from '@/common/events';

    const props = defineProps<{
        quote: QuoteResource;
    }>();

    const companyMemberStore = useCompanyInfoStore();
    onMounted(async () => await companyMemberStore.getCompanyMembers(props.quote.client.company.id));

    const screenSizeStore = useScreenSizeStore();
    const {isDesktopScreen} = storeToRefs(screenSizeStore);

    const quoteViewStore = useClientQuoteViewStore();
    const {isClientQuoteOwner} = storeToRefs(quoteViewStore);

    const clientFullName = computed(() => `${props.quote.client.firstName} ${props.quote.client.lastName}`);
    const freelancerName = computed(() => `${props.quote.freelancer.firstName} ${props.quote.freelancer.lastName}`);

    const computedFreelancerInfo = computed(() => {
        return {
            profileId: props.quote.freelancer.profileId,
            fullName: freelancerName.value,
            headline: props.quote.freelancer.headLine,
            photoUrl: props.quote.freelancer.photoUrl,
        };
    });

    function handleOpenDialog() {
        eventBus.emit('openChangeQuoteOwnerModal');
    }

    function displayFreelancerProfileDrawer() {
        eventBus.emit('showFreelancerDrawer', {tab: FreelancerDrawerTab.Profile});
    }

    function displayFreelancerMessengerDrawer() {
        eventBus.emit('showFreelancerDrawer', {tab: FreelancerDrawerTab.Messages});
    }

    onUnmounted(() => {
        eventBus.off('openChangeQuoteOwnerModal');
        eventBus.off('showFreelancerDrawer');
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .full-width {
        width: 100%;
    }

    .team-panel {
        &__role {
            @include text.getFontBySize(primary-300);

            color: var(--joy-color-neutral-40);
            margin-bottom: var(--joy-core-spacing-1);
        }

        &__info-container {
            width: 200px;
            align-items: center;
            flex-wrap: nowrap;

            &__about {
                @include text.getFontBySize(primary-300);

                margin-left: var(--joy-core-spacing-2);
            }

            &__name {
                @include text.getFontBySize(primary-350);

                margin-bottom: 0;
            }

            &__job-title {
                @include text.getFontBySize(primary-300);
            }

            &__avatar {
                flex: 0 0 auto;
            }
        }

        &__actions {
            gap: var(--joy-core-spacing-2);
        }
    }
</style>
